import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Camera,
  RotateCcw,
  Rocket,
  Image as ImageIcon,
  Check,
  X,
  Grid3x3,
  User,
  Sparkles,
  SlashSquare,
} from "lucide-react";

/**
 * Guest capture: camera view with frame overlay, composite on canvas, upload.
 */
export default function GuestCapturePage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [guestName] = useState(
    localStorage.getItem(`guest_name_${slug}`) || ""
  );
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const frameImgRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const [captured, setCaptured] = useState(null); // dataURL
  const [uploading, setUploading] = useState(false);
  const [facing, setFacing] = useState("environment");
  const [cameraError, setCameraError] = useState(null);
  const [frameIndex, setFrameIndex] = useState(0);
  const [caption, setCaption] = useState("");
  const [useFrame, setUseFrame] = useState(true);
  const [avatarPath, setAvatarPath] = useState(
    localStorage.getItem(`guest_avatar_${slug}`) || "",
  );
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [batchQueue, setBatchQueue] = useState([]); // [{dataUrl}]
  const [batchProgress, setBatchProgress] = useState(null); // {done, total}
  const [successCount, setSuccessCount] = useState(0);
  const frameRefs = useRef([]);
  const avatarInputRef = useRef(null);

  const getFrames = () =>
    event?.frames && event.frames.length > 0
      ? event.frames
      : event?.frame_path
      ? [event.frame_path]
      : [];

  useEffect(() => {
    if (!guestName) {
      nav(`/e/${slug}`);
      return;
    }
    api
      .get(`/public/events/${slug}`)
      .then((r) => setEvent(r.data))
      .catch(() => toast.error("Evento não encontrado"));
    // eslint-disable-next-line
  }, [slug]);

  const startCamera = async (mode) => {
    setCameraError(null);
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: mode,
          width: { ideal: 1280 },
          height: { ideal: 1280 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      setCameraError(
        "Não conseguimos acessar a câmera. Você pode enviar uma foto da galeria."
      );
    }
  };

  useEffect(() => {
    if (event) startCamera(facing);
    return () => {
      if (streamRef.current)
        streamRef.current.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line
  }, [event, facing]);

  const capture = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      toast.error("Câmera ainda não carregou");
      return;
    }
    // 9:16 crop from video
    const targetW = 1080;
    const targetH = 1920;
    const targetAspect = targetW / targetH;
    const videoAspect = video.videoWidth / video.videoHeight;
    let sw, sh, sx, sy;
    if (videoAspect > targetAspect) {
      sh = video.videoHeight;
      sw = sh * targetAspect;
      sx = (video.videoWidth - sw) / 2;
      sy = 0;
    } else {
      sw = video.videoWidth;
      sh = sw / targetAspect;
      sx = 0;
      sy = (video.videoHeight - sh) / 2;
    }

    const canvas = canvasRef.current;
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, targetW, targetH);

    // Draw frame overlay (selected) unless user turned off
    const frames = getFrames();
    const activeFrame = frameRefs.current[frameIndex];
    if (useFrame && frames.length > 0 && activeFrame?.complete) {
      ctx.drawImage(activeFrame, 0, 0, targetW, targetH);
    }

    // Watermark for VIP events
    if (event?.watermark_enabled) {
      ctx.font = "bold 34px Outfit, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.textAlign = "right";
      ctx.shadowColor = "rgba(0,0,0,0.5)";
      ctx.shadowBlur = 6;
      ctx.fillText("Festagram", targetW - 32, targetH - 40);
      ctx.shadowBlur = 0;
    }

    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setCaptured(dataUrl);
  };

  const pickFromGallery = () => {
    fileInputRef.current?.click();
  };

  const composeImage = (img) =>
    new Promise((resolve) => {
      const targetW = 1080;
      const targetH = 1920;
      const targetAspect = targetW / targetH;
      const imgAspect = img.width / img.height;
      let sw, sh, sx, sy;
      if (imgAspect > targetAspect) {
        sh = img.height;
        sw = sh * targetAspect;
        sx = (img.width - sw) / 2;
        sy = 0;
      } else {
        sw = img.width;
        sh = sw / targetAspect;
        sx = 0;
        sy = (img.height - sh) / 2;
      }
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);
      const frames = getFrames();
      const activeFrame = frameRefs.current[frameIndex];
      if (useFrame && frames.length > 0 && activeFrame?.complete) {
        ctx.drawImage(activeFrame, 0, 0, targetW, targetH);
      }
      if (event?.watermark_enabled) {
        ctx.font = "bold 34px Outfit, sans-serif";
        ctx.fillStyle = "rgba(255,255,255,0.85)";
        ctx.textAlign = "right";
        ctx.fillText("Festagram", targetW - 32, targetH - 40);
      }
      resolve(canvas.toDataURL("image/jpeg", 0.9));
    });

  const handlePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    const results = [];
    for (const file of files) {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = "anonymous";
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = URL.createObjectURL(file);
      }).catch(() => null);
      if (img) results.push(await composeImage(img));
    }
    if (results.length === 1) {
      setCaptured(results[0]);
      setBatchQueue([]);
    } else if (results.length > 1) {
      setBatchQueue(results);
      setCaptured(null);
    }
  };

  const uploadOne = async (dataUrl) => {
    const blob = await fetch(dataUrl).then((r) => r.blob());
    const fd = new FormData();
    fd.append("guest_name", guestName);
    fd.append("caption", caption.trim().slice(0, 150));
    fd.append("guest_id", localStorage.getItem("gve_guest_id") || "");
    fd.append("avatar_path", avatarPath || "");
    fd.append("file", blob, "photo.jpg");
    await api.post(`/public/events/${slug}/photos`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const send = async () => {
    const queue = batchQueue.length > 0 ? batchQueue : captured ? [captured] : [];
    if (queue.length === 0) return;
    setUploading(true);
    setBatchProgress({ done: 0, total: queue.length });
    let ok = 0;
    for (let i = 0; i < queue.length; i++) {
      try {
        await uploadOne(queue[i]);
        ok += 1;
        setBatchProgress({ done: ok, total: queue.length });
      } catch (err) {
        // continue; final count reflects success only
      }
    }
    setSuccessCount(ok);
    setUploading(false);
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/public/events/${slug}/guest-avatar`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setAvatarPath(data.avatar_path);
      localStorage.setItem(`guest_avatar_${slug}`, data.avatar_path);
      toast.success("Foto de perfil salva!");
    } catch (err) {
      toast.error("Erro ao enviar foto de perfil");
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <div className="min-h-[100dvh] text-white flex flex-col bg-black">
      <div className="flex items-center justify-between px-4 py-3">
        <Link
          to={`/e/${slug}`}
          className="text-xs text-white/50 hover:text-white transition-colors"
          data-testid="capture-back"
        >
          ← Voltar
        </Link>
        <div className="text-center flex-1 mx-3">
          <div className="text-xs uppercase tracking-[0.2em] text-white/40 truncate">
            {event?.name || "..."}
          </div>
          <button
            onClick={() => avatarInputRef.current?.click()}
            className="mt-1 inline-flex items-center gap-1.5 text-xs text-[#FF007F] hover:text-white transition-colors"
            data-testid="edit-avatar-btn"
            title="Definir foto de perfil"
          >
            {avatarPath ? (
              <img
                src={fileUrl(avatarPath)}
                alt=""
                className="w-6 h-6 rounded-full object-cover border border-[#FF007F]"
              />
            ) : (
              <User className="w-3.5 h-3.5" />
            )}
            {guestName}
          </button>
        </div>
        <Link
          to={`/e/${slug}/gallery`}
          className="text-white/50 hover:text-white transition-colors"
          title="Galeria"
        >
          <Grid3x3 className="w-5 h-5" />
        </Link>
      </div>

      {/* Hidden frame images for canvas compositing (support multiple) */}
      {(() => {
        const frames = getFrames();
        return frames.map((fp, i) => (
          <img
            key={fp}
            ref={(el) => (frameRefs.current[i] = el)}
            src={fileUrl(fp)}
            alt=""
            crossOrigin="anonymous"
            className="hidden"
            data-testid={`hidden-frame-img-${i}`}
          />
        ));
      })()}

      <div className="flex-1 flex items-center justify-center px-4 pb-4">
        <div className="w-full max-w-[380px]">
          <div className="relative aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 bg-[#0B0914]">
            {captured ? (
              <img
                src={captured}
                alt="Capturada"
                className="w-full h-full object-cover"
                data-testid="captured-preview"
              />
            ) : (
              <>
                {getFrames().length > 0 && (
                  <button
                    onClick={() => setUseFrame((v) => !v)}
                    className={`absolute top-2 right-2 z-10 text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full font-bold transition-all ${
                      useFrame
                        ? "bg-[#FF007F] text-white"
                        : "bg-black/70 text-white/70 border border-white/20"
                    }`}
                    data-testid="toggle-frame-btn"
                  >
                    {useFrame ? "Moldura ON" : "Moldura OFF"}
                  </button>
                )}
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  data-testid="camera-video"
                />
                {(() => {
                  const frames = getFrames();
                  return useFrame && frames[frameIndex] ? (
                    <img
                      src={fileUrl(frames[frameIndex])}
                      alt="Moldura"
                      className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                      data-testid="active-frame-preview"
                    />
                  ) : null;
                })()}
                {cameraError && (
                  <div className="absolute inset-0 flex items-center justify-center p-6 text-center bg-black/70">
                    <p className="text-white/70 text-sm">{cameraError}</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Frame picker (only if multiple frames) */}
          {(() => {
            const frames = getFrames();
            if (frames.length <= 1 || captured) return null;
            return (
              <div className="mt-3 flex items-center justify-center gap-2" data-testid="frame-picker">
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 mr-1">
                  Moldura
                </span>
                {frames.map((fp, i) => (
                  <button
                    key={fp}
                    onClick={() => setFrameIndex(i)}
                    className={`w-10 h-14 rounded-md overflow-hidden border-2 transition-all ${
                      frameIndex === i
                        ? "border-[#FF007F] shadow-[0_0_12px_rgba(255,0,127,0.6)]"
                        : "border-white/20 opacity-60 hover:opacity-100"
                    }`}
                    data-testid={`pick-frame-${i}`}
                    title={`Moldura ${i + 1}`}
                  >
                    <img
                      src={fileUrl(fp)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            );
          })()}

          <canvas ref={canvasRef} className="hidden" />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePicked}
            className="hidden"
            data-testid="file-input"
          />
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            onChange={uploadAvatar}
            className="hidden"
            data-testid="avatar-input"
          />

          {/* Caption input (only when photo is captured or batch queued) */}
          {(captured || batchQueue.length > 0) && !successCount && (
            <div className="mt-4" data-testid="caption-block">
              {batchQueue.length > 1 && (
                <div className="mb-3">
                  <div className="text-xs uppercase tracking-[0.15em] text-white/50 mb-2">
                    {batchQueue.length} fotos selecionadas
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {batchQueue.map((dataUrl, i) => (
                      <img
                        key={i}
                        src={dataUrl}
                        alt=""
                        className="w-14 h-24 rounded-md object-cover flex-shrink-0 border border-white/10"
                        data-testid={`batch-thumb-${i}`}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className="relative">
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value.slice(0, 150))}
                  maxLength={150}
                  rows={2}
                  placeholder={
                    batchQueue.length > 1
                      ? "Legenda pra todas as fotos... (opcional)"
                      : "Escreve uma legenda... (opcional)"
                  }
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 pr-14 text-sm text-white placeholder:text-white/35 resize-none focus:outline-none focus:border-[#FF007F]/60 transition-colors"
                  data-testid="caption-input"
                />
                <span
                  className={`absolute bottom-2 right-3 text-[10px] font-mono ${
                    caption.length > 130 ? "text-[#FF007F]" : "text-white/40"
                  }`}
                  data-testid="caption-counter"
                >
                  {caption.length}/150
                </span>
              </div>
            </div>
          )}

          {/* SUCCESS SCREEN */}
          {successCount > 0 && (
            <div
              className="mt-6 glass rounded-2xl p-8 text-center"
              data-testid="upload-success-screen"
            >
              <div className="w-16 h-16 rounded-full bg-[#39FF14]/15 border-2 border-[#39FF14] flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-[#39FF14]" />
              </div>
              <h3 className="font-display font-black text-2xl tracking-tighter">
                {successCount === 1
                  ? "Foto enviada com sucesso!"
                  : `${successCount} fotos enviadas com sucesso!`}
              </h3>
              <p className="text-white/60 text-sm mt-2">
                Já apareceu no feed do evento 🎉
              </p>
              <div className="mt-6 flex flex-col gap-2">
                <button
                  onClick={() => nav(`/e/${slug}/gallery`)}
                  className="btn-neon w-full"
                  data-testid="success-view-gallery"
                >
                  <Grid3x3 className="w-4 h-4" /> Ver no feed
                </button>
                <button
                  onClick={() => {
                    setSuccessCount(0);
                    setCaptured(null);
                    setBatchQueue([]);
                    setCaption("");
                  }}
                  className="btn-cyan w-full"
                  data-testid="success-send-another"
                >
                  <Camera className="w-4 h-4" /> Enviar mais fotos
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          {!successCount && (
          <div className="mt-6 flex items-center justify-center gap-4">
            {!captured && batchQueue.length === 0 ? (
              <>
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={pickFromGallery}
                    className="w-14 h-14 rounded-full glass hover-lift flex items-center justify-center"
                    title="Enviar 1 ou várias da galeria"
                    data-testid="pick-gallery-btn"
                  >
                    <ImageIcon className="w-5 h-5 text-white/80" />
                  </button>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-white/50">
                    Galeria
                  </span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={capture}
                    className="w-20 h-20 rounded-full bg-white flex items-center justify-center pulse-ring hover:scale-105 transition-transform"
                    data-testid="capture-btn"
                  >
                    <div className="w-16 h-16 rounded-full bg-white border-4 border-black" />
                  </button>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-white/50">
                    Tirar foto
                  </span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={() =>
                      setFacing((f) => (f === "user" ? "environment" : "user"))
                    }
                    className="w-14 h-14 rounded-full glass hover-lift flex items-center justify-center"
                    title="Virar câmera"
                    data-testid="flip-camera-btn"
                  >
                    <RotateCcw className="w-5 h-5 text-white/80" />
                  </button>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-white/50">
                    Virar
                  </span>
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    setCaptured(null);
                    setBatchQueue([]);
                  }}
                  className="btn-cyan"
                  data-testid="retake-btn"
                >
                  <X className="w-4 h-4" /> Refazer
                </button>
                <button
                  onClick={send}
                  disabled={uploading}
                  className="btn-neon"
                  data-testid="send-photo-btn"
                >
                  {uploading ? (
                    batchProgress
                      ? `Enviando ${batchProgress.done + 1}/${batchProgress.total}...`
                      : "Enviando..."
                  ) : (
                    <>
                      <Rocket className="w-4 h-4" />{" "}
                      {batchQueue.length > 1
                        ? `Enviar ${batchQueue.length} fotos`
                        : "Enviar"}
                    </>
                  )}
                </button>
              </>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
